import type { Breakpoint } from '@mui/material'
import { Box } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useScrollNavigation } from './ScrollNavigationContext'

interface ScrollNavigationTargetItemProps {
  id: string
  children: React.ReactNode
}

// Height of the sticky top header (LegacyNavbar / HorizontalMenu, both 56px).
// Call-site `scrollMarginTopOffset` values are offsets relative to this base,
// so targets clear the header instead of landing under it.
const STICKY_HEADER_HEIGHT = 56

/**
 * A component that wraps content that should be tracked by the scroll navigation system.
 * Use this component to mark sections that should be highlighted in the navigation when scrolled into view
 * and should scroll into view when clicked in the navigation.
 */
export const ScrollNavigationTargetItem: React.FC<ScrollNavigationTargetItemProps> = ({ id, children }) => {
  const { registerSectionRef, handleSectionInView, scrollMarginTopOffset } = useScrollNavigation()

  const { ref: inViewRef } = useInView({
    threshold: 0.3,
    rootMargin: '-10% 0px -10% 0px',
    onChange: inView => handleSectionInView(id, inView),
  })

  const setRefs = useCallback(
    (node: HTMLElement | null) => {
      registerSectionRef(id, node)
      inViewRef(node)
    },
    [id, inViewRef, registerSectionRef],
  )

  const scrollMarginTop = useMemo(() => {
    if (typeof scrollMarginTopOffset === 'number') {
      return `${STICKY_HEADER_HEIGHT + scrollMarginTopOffset}px`
    }

    return Object.fromEntries(
      Object.entries(scrollMarginTopOffset)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, `${STICKY_HEADER_HEIGHT + value}px`]),
    ) as Partial<Record<Breakpoint, string>>
  }, [scrollMarginTopOffset])

  return (
    <Box
      ref={setRefs}
      sx={{
        scrollMarginTop,
      }}
    >
      {children}
    </Box>
  )
}
